// packages/web/src/lib/edenRealtimeClient.ts — V19b-3: the browser half of
// the realtime voice seam (docs/console/v19b-the-client-surface.md,
// docs/console/v18-eden-onboarding.md V18c). Everything server-side —
// POST /api/eden/realtime/session (mints the ephemeral secret) and POST
// /api/eden/realtime/tool-calls (the V11 command-boundary dispatcher) — is
// already built, deployed and verified live. This module's ONLY job is the
// WebRTC handshake against OpenAI's Realtime API and relaying whatever the
// model asks for to that dispatcher — it never decides an outcome itself.
//
// ⚑ INV-EDEN-8 BY CONSTRUCTION: this module never receives, resolves or
// touches the vaulted OpenAI API key (an `sk-…` value) — only the
// short-lived `ephemeralSecret` (an `ek_…` value) the server already
// minted, handed in as a plain argument. There is no vault import, no
// fetch to anything that could return the real key, anywhere in this file.
//
// ⚑ INV-EDEN-5 BY CONSTRUCTION: `handleServerEvent` below does exactly ONE
// thing with a model function-call event — hand `{clientToolCallId, name,
// arguments}` to the caller-supplied `relay` function VERBATIM. There is no
// switch/if on `event.name` anywhere in this file that maps a tool name to
// a local action; the browser cannot execute a tool even by accident,
// because the code path to do so does not exist. A name the server itself
// does not recognise still gets relayed — the REFUSAL happens server-side
// (dispatchRealtimeToolCall's own TOOL_NAME_TO_COMMAND allowlist), not here.
//
// The realtime `calls` endpoint contract used below (`POST
// https://api.openai.com/v1/realtime/calls?model=<model>`, `Content-Type:
// application/sdp`, body = the offer SDP text, response body = the answer
// SDP text; function-call events arrive on the data channel as
// `response.function_call_arguments.done` carrying `call_id`/`name`/
// `arguments`, answered with a `conversation.item.create` /
// `function_call_output` + `response.create` pair) is the PUBLICLY
// DOCUMENTED OpenAI Realtime WebRTC protocol as of this chunk's build —
// NOT independently re-verified against a live call this session (this
// chunk ran zero live OpenAI sessions; see its own build report for why).
// Flagged exactly like realtimeSession.ts's own header flags its
// less-than-fully-verified shapes, rather than claimed equally certain.

/** What the server dispatcher (POST /api/eden/realtime/tool-calls, via
 *  lib/api.ts's dispatchEdenRealtimeToolCall) can answer — mirrors
 *  EdenRealtimeToolCallOutcome there. Kept as its own narrow type here so
 *  this module depends on a SHAPE, not on lib/api.ts's fetch plumbing —
 *  the relay function is injected by the caller (EdenOnboarding.tsx),
 *  never imported directly, which is also what makes this module testable
 *  without a network. */
export type EdenRealtimeRelayResult =
  | { status: 'executed'; receipt: string }
  | { status: 'refused'; reason: string }
  | { status: 'budget_exceeded'; receipt: string }
  | { status: 'invalid' }
  | { status: 'expired' };

export type EdenRealtimeRelayFn = (call: {
  clientToolCallId: string;
  name: string;
  arguments: unknown;
}) => Promise<EdenRealtimeRelayResult>;

/** Why a session ended — always named, never a silent hang (INV-EDEN-11's
 *  own "never a silent hang"). 'closed' is the caller hanging up on
 *  purpose (e.g. leaving the screen); every other reason is something
 *  that happened TO the session. */
export type EdenRealtimeEndReason = 'budget_exceeded' | 'invalid' | 'expired' | 'dropped' | 'closed';

export interface EdenRealtimeCallbacks {
  /** A tool call the server actually executed — the caller renders this as
   *  a Knot proposal (a fact/observation card), never re-derives it. */
  onToolExecuted?: (call: { name: string; arguments: unknown; receipt: string }) => void;
  /** The browser refused to play Eden's voice (autoplay policy, or no output
   *  device). The session is otherwise fine -- but the client is sitting in
   *  silence, so this must reach the screen, never a swallowed catch. */
  onVoicePlaybackBlocked?: (detail: string) => void;
  /** A tool call the server refused (unrecognised name, invalid args, a
   *  scope violation) — named and calm, never surfaced as a crash. */
  onToolRefused?: (call: { name: string; reason: string }) => void;
  /** The data channel is open and the agent can be talked to. */
  onConnected?: () => void;
  /** The session is over. `receipt` is present for 'budget_exceeded' —
   *  INV-EDEN-11: names what happened and everything captured up to this
   *  point stands, untouched, in the caller's own state. */
  onEnded: (reason: EdenRealtimeEndReason, receipt?: string) => void;
}

/** The narrow slice of RTCPeerConnection/RTCDataChannel this module
 *  actually uses — real in the browser, a hand-built stand-in in tests
 *  (happy-dom carries no WebRTC globals at all, so a test double is the
 *  only way to exercise this module's own logic without a network). */
export interface EdenRealtimePeerConnectionLike {
  addTrack: (track: MediaStreamTrack, stream: MediaStream) => unknown;
  createDataChannel: (label: string) => EdenRealtimeDataChannelLike;
  createOffer: () => Promise<{ sdp?: string }>;
  setLocalDescription: (desc: { sdp?: string }) => Promise<void>;
  setRemoteDescription: (desc: { type: 'answer'; sdp: string }) => Promise<void>;
  addEventListener: (type: string, listener: (ev: unknown) => void) => void;
  close: () => void;
  iceConnectionState?: string;
}
export interface EdenRealtimeDataChannelLike {
  send: (data: string) => void;
  close: () => void;
  addEventListener: (type: string, listener: (ev: unknown) => void) => void;
  readyState: string;
}

export interface EdenRealtimeConnectDeps {
  createPeerConnection?: () => EdenRealtimePeerConnectionLike;
  fetchImpl?: typeof fetch;
}

export interface EdenRealtimeConnectArgs {
  ephemeralSecret: string;
  model: string;
  stream: MediaStream;
  relay: EdenRealtimeRelayFn;
}

export interface EdenRealtimeSessionHandle {
  /** Hangs up deliberately — closes the data channel and peer connection
   *  and fires onEnded('closed'). Idempotent: a second call is a no-op. */
  close: () => void;
}

function defaultCreatePeerConnection(): EdenRealtimePeerConnectionLike {
  // Real browsers only — this module never runs the default in a test.
  return new RTCPeerConnection() as unknown as EdenRealtimePeerConnectionLike;
}

/**
 * Opens the WebRTC session against OpenAI's Realtime API using the
 * server-minted ephemeral secret, and relays every model tool call to the
 * server (INV-EDEN-5) until the session ends for any reason (INV-EDEN-11).
 * Never executes a tool locally, never sees or sends the real API key
 * (INV-EDEN-8).
 */
export async function connectEdenRealtimeVoice(
  args: EdenRealtimeConnectArgs,
  callbacks: EdenRealtimeCallbacks,
  deps: EdenRealtimeConnectDeps = {}
): Promise<EdenRealtimeSessionHandle> {
  const createPC = deps.createPeerConnection ?? defaultCreatePeerConnection;
  const fetchFn = deps.fetchImpl ?? fetch;

  const pc = createPC();
  let ended = false;

  function endSession(reason: EdenRealtimeEndReason, receipt?: string): void {
    if (ended) return;
    ended = true;
    try {
      dc.close();
    } catch {
      /* already closed — never let a teardown error mask the real reason */
    }
    try {
      if (remoteAudio) {
        remoteAudio.pause();
        remoteAudio.srcObject = null;
        remoteAudio.remove();
        remoteAudio = null;
      }
    } catch {
      /* teardown of the audio element must never mask the real end reason */
    }
    try {
      pc.close();
    } catch {
      /* same */
    }
    callbacks.onEnded(reason, receipt);
  }

  for (const track of args.stream.getAudioTracks()) {
    pc.addTrack(track, args.stream);
  }

  // ⚑ EDEN'S OWN VOICE. Without this the session works perfectly in every
  // measurable way and the client hears NOTHING: the model's audio arrives at
  // the peer connection and is discarded for want of anywhere to play it.
  //
  // This shipped. Huan found it in ninety seconds on an Android phone -- he
  // talked, Eden replied, the reply went nowhere, and because Eden was almost
  // certainly ASKING him something, it then waited for an answer he could not
  // know was wanted and captured nothing. Every automated check passed through
  // it: the walk asserted `bytesReceived > 0` on the RTCPeerConnection, which
  // is true of a connection whose audio is thrown away. Bytes reaching a peer
  // connection is not sound reaching a speaker.
  //
  // `playsInline` matters on iOS: without it Safari can take audio fullscreen.
  // Playback is attempted inside the user gesture that started the session, so
  // autoplay policy allows it -- but a rejection is surfaced, never swallowed,
  // because a silent Eden must never again look like a working one.
  let remoteAudio: HTMLAudioElement | null = null;
  pc.addEventListener('track', (event) => {
    const [remoteStream] = (event as RTCTrackEvent).streams;
    if (!remoteStream) return;
    if (!remoteAudio) {
      remoteAudio = document.createElement('audio');
      remoteAudio.autoplay = true;
      remoteAudio.setAttribute('playsinline', '');
      remoteAudio.style.display = 'none';
      document.body.appendChild(remoteAudio);
    }
    remoteAudio.srcObject = remoteStream;
    void remoteAudio.play().catch((err: unknown) => {
      callbacks.onVoicePlaybackBlocked?.(String(err));
    });
  });

  const dc = pc.createDataChannel('oai-events');

  dc.addEventListener('open', () => {
    // ⚑ EDEN SPEAKS FIRST (Huan, 2026-09-18). Until this, `response.create`
    // was only ever sent AFTER a tool call, so the model never opened its
    // mouth until the client spoke -- two parties each waiting for the other,
    // which is exactly what Huan met: a listening dot and silence.
    //
    // The client's voice is how they learn they may speak at all. The screen
    // is deliberately not allowed to teach that with a caption (UI must be
    // self-teaching) -- so the greeting IS the instruction, and it has to
    // arrive unprompted.
    try {
      dc.send(JSON.stringify({ type: 'response.create' }));
    } catch {
      /* a channel that dies between open and this send ends the session
         through its own 'close' handler -- never mask that with a throw */
    }
    callbacks.onConnected?.();
  });
  // A data channel that closes without endSession having run first is a
  // DROPPED connection, not a deliberate hangup — 'closed' is reserved for
  // this module's own close() below.
  dc.addEventListener('close', () => endSession('dropped'));
  pc.addEventListener('iceconnectionstatechange', () => {
    const state = pc.iceConnectionState;
    if (state === 'failed' || state === 'disconnected') endSession('dropped');
  });

  function sendToolOutput(callId: string, output: string): void {
    if (dc.readyState !== 'open') return;
    dc.send(
      JSON.stringify({
        type: 'conversation.item.create',
        item: { type: 'function_call_output', call_id: callId, output },
      })
    );
    dc.send(JSON.stringify({ type: 'response.create' }));
  }

  // ⚑ INV-EDEN-5, the whole invariant, in one function: the ONLY thing
  // done with `event.name` is pass it to `relay` unexamined. Everything
  // below branches on the SERVER's own answer (result.status), never on
  // what the model asked for.
  async function handleFunctionCall(callId: string, name: string, rawArguments: string | undefined): Promise<void> {
    let parsedArgs: unknown = {};
    if (rawArguments) {
      try {
        parsedArgs = JSON.parse(rawArguments);
      } catch {
        parsedArgs = {};
      }
    }
    const result = await args.relay({ clientToolCallId: callId, name, arguments: parsedArgs });
    switch (result.status) {
      case 'executed':
        sendToolOutput(callId, result.receipt);
        callbacks.onToolExecuted?.({ name, arguments: parsedArgs, receipt: result.receipt });
        return;
      case 'refused':
        sendToolOutput(callId, result.reason);
        callbacks.onToolRefused?.({ name, reason: result.reason });
        return;
      case 'budget_exceeded':
        endSession('budget_exceeded', result.receipt);
        return;
      case 'invalid':
      case 'expired':
        endSession(result.status);
        return;
    }
  }

  dc.addEventListener('message', (ev) => {
    // A message arriving after the session has already ended (budget,
    // invalid/expired, a deliberate close, a drop) is dropped, not
    // processed — endSession() already tore down the channel; relaying
    // one more call here would either double-fire a callback or crash on
    // whatever state a caller's `relay` is in post-teardown.
    if (ended) return;
    const raw = (ev as { data?: unknown }).data;
    if (typeof raw !== 'string') return;
    let event: unknown;
    try {
      event = JSON.parse(raw);
    } catch {
      return;
    }
    const e = event as { type?: string; call_id?: string; name?: string; arguments?: string };
    if (e.type === 'response.function_call_arguments.done' && typeof e.call_id === 'string' && typeof e.name === 'string') {
      void handleFunctionCall(e.call_id, e.name, e.arguments);
    }
  });

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  if (!offer.sdp) throw new Error('local SDP offer was empty — cannot open a realtime session');

  // Everything from here to the answer being accepted is ONE failure
  // domain: a network error, a non-2xx, or an SDP answer the browser's own
  // WebRTC stack refuses (setRemoteDescription can throw on a malformed or
  // incompatible answer, same as any other step here) must ALL tear the
  // connection down the same way — found by the real-Chromium walk
  // (scripts/walkEdenRealtimeV19b3.ts): an earlier version of this
  // function only wrapped the fetch itself, so a setRemoteDescription
  // rejection left `pc` open forever, an orphaned connection this
  // function's own caller had no handle to close.
  try {
    const resp = await fetchFn(`https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(args.model)}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${args.ephemeralSecret}`,
        'Content-Type': 'application/sdp',
      },
      body: offer.sdp,
    });
    if (!resp.ok) {
      throw new Error(`realtime handshake failed (HTTP ${resp.status})`);
    }
    const answerSdp = await resp.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
  } catch (err) {
    endSession('dropped');
    throw err;
  }

  return {
    close: () => endSession('closed'),
  };
}
